import { HttpStatusCode } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GcvAdminApi } from 'api';
import { ApiError } from 'api/api-util/api-error';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { DISPENSARY_KEY, SFTP_SALES_PROVIDER_KEY, SFTP_SALES_PROVIDERS_KEY } from 'tanstack-hooks/keys';

export function useUpsertSftpSalesLocationMapping(providerId: string) {
  const gcvAdminApi = new GcvAdminApi();
  const queryClient = useQueryClient();

  const { mutateAsync: upsertSftpSalesLocationMapping, isPending: isUpsertingSftpSalesLocationMapping } =
    useMutation({
      mutationKey: [SFTP_SALES_PROVIDER_KEY, providerId, 'upsertLocationMapping'],
      // the UI never overwrites a CRB's POS config; confirmPosOverwrite is for scripts only
      mutationFn: ({ locationId, crbId }: { locationId: string; crbId: string }) =>
        gcvAdminApi.upsertSftpSalesLocationMapping(providerId, locationId, {
          crbId,
          confirmPosOverwrite: false
        }),
      onSuccess: (_, { crbId }) => {
        queryClient.invalidateQueries({ queryKey: [SFTP_SALES_PROVIDERS_KEY] });
        queryClient.invalidateQueries({ queryKey: [SFTP_SALES_PROVIDER_KEY, providerId] });
        queryClient.invalidateQueries({ queryKey: [DISPENSARY_KEY, crbId] });
      },
      // a 409 message names the existing mapping that blocks the save (GS-19184)
      onError: (error) => {
        getSnackbarStore().showErrorSnackbarMessage(
          error instanceof ApiError && error.response?.status === HttpStatusCode.CONFLICT
            ? error.response.message
            : 'There was an issue saving the location mapping. Please try again.'
        );
      },
      meta: {
        snackbarSuccessMessage: 'Location mapping saved successfully.'
      }
    });

  return { upsertSftpSalesLocationMapping, isUpsertingSftpSalesLocationMapping };
}
